import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Renders children at document.body level with `position: fixed`, anchored
 * below (or above, if no room) a trigger element. Escapes any stacking
 * context — solves the grid-cell popover that gets clipped behind sticky
 * rows in the schedule.
 *
 * Closes when:
 *   - Escape pressed
 *   - User clicks outside both the trigger and the popover content
 */
export default function PopoverPortal({ anchorRef, open, onClose, children, align = "left" }) {
  const popRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: "bottom" });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    // Default below the trigger; flip above if it would clip.
    const popH = popRef.current?.offsetHeight || 32;
    const placement = r.bottom + popH + 8 > vh ? "top" : "bottom";
    const top = placement === "bottom" ? r.bottom + 4 : r.top - popH - 4;
    const left = align === "right" ? r.right : r.left;
    setCoords({ top, left, placement });
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => {
      if (!popRef.current || !anchorRef.current) return;
      if (popRef.current.contains(e.target) || anchorRef.current.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: coords.top,
        left: align === "right" ? undefined : coords.left,
        right: align === "right" ? window.innerWidth - coords.left : undefined,
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
